/**
 * 고용형태 이름 — ko / en / ja (2026-09-13 · 서버 #1222 수습 · 인턴 추가).
 *
 * 서버 코드 목록(`EMPLOYMENT_TYPES`)과 **같은 순서 · 같은 값**이다:
 *   REGULAR · CONTRACT · PARTTIME · PROBATION · INTERN
 * 회사가 이름을 바꾸거나 끄는 것은 `useCompanyCodes('hr').apply('employment_type', …)` 가 이 목록 위에 덮는다 —
 * 여기는 «회사 코드를 못 받을 때» 의 기본 이름이다.
 *
 * ★ 한국어 전용 `EMPLOYMENT_TYPE_LABEL`(api/hr-employee.ts)은 종전 화면(명부 legacy · 직원 관리)이 그대로 쓴다.
 *   두 곳의 한국어는 같아야 한다 — `employment-type-i18n.test.ts` 가 대조한다.
 */
import { getSharedLang, FALLBACK_LANG, type Lang } from './base-i18n';

export const EMPLOYMENT_TYPE_CODES = ['REGULAR', 'CONTRACT', 'PARTTIME', 'PROBATION', 'INTERN'] as const;
export type EmploymentTypeCode = (typeof EMPLOYMENT_TYPE_CODES)[number];

const MESSAGES: Record<EmploymentTypeCode, Record<Lang, string>> = {
  REGULAR: { ko: '정규직', en: 'Full-time', ja: '正社員' },
  CONTRACT: { ko: '계약직', en: 'Contract', ja: '契約社員' },
  PARTTIME: { ko: '단시간', en: 'Part-time', ja: '短時間' },
  PROBATION: { ko: '수습', en: 'Probation', ja: '試用' },
  INTERN: { ko: '인턴', en: 'Intern', ja: 'インターン' },
};

const isCode = (code: string): code is EmploymentTypeCode => (EMPLOYMENT_TYPE_CODES as readonly string[]).includes(code);

/** 모르는 코드는 코드 그대로 — 서버가 새 값을 먼저 보내도 화면이 비지 않는다. */
export function tEmploymentType(code: string, lang: Lang = getSharedLang()): string {
  if (!isCode(code)) return code;
  const m = MESSAGES[code];
  return m[lang] || m.en || m[FALLBACK_LANG];
}

/** 칩 · 선택지용 기본 목록(서버 순서). */
export function employmentTypeOptions(lang: Lang = getSharedLang()): Array<{ key: EmploymentTypeCode; label: string }> {
  return EMPLOYMENT_TYPE_CODES.map((key) => ({ key, label: tEmploymentType(key, lang) }));
}
